/**
 * IntercomProvider
 * 
 * Manages Intercom lifecycle - specifically handles shutdown on logout.
 * Intercom boots lazily only when user clicks Live Chat button.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './auth/AuthProvider';
import { shutdownIntercom, isIntercomConfigured } from '../lib/intercom';

export function IntercomProvider({ children }: { children: React.ReactNode }) {
  const { user, authReady } = useAuth();
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait for auth to be ready
    if (!authReady) return;

    // Skip if Intercom isn't configured
    if (!isIntercomConfigured()) return;

    // Track user changes
    if (user) {
      lastUserIdRef.current = user.id;
    }

    // User logged out - shutdown Intercom to prevent session leakage
    if (!user && lastUserIdRef.current) {
      shutdownIntercom();
      lastUserIdRef.current = null;
    }
  }, [authReady, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (lastUserIdRef.current) {
        shutdownIntercom();
      }
    };
  }, []);

  return <>{children}</>;
}

export default IntercomProvider;
